// Gameplay layer: choice dilemmas, mayor requests, weekend rush, year report + perks, visitor milestones.
import { store, pushNews } from '../core/store.js';
import { bus } from '../core/bus.js';

const DILEMMAS = [
  { text: 'A circus offers $15,000 for one of your animals.', a: { label: 'Sell ($15k)', money: 15000, reputation: -8, news: 'The circus deal made the papers — visitors are not amused.' }, b: { label: 'Refuse (+rep)', reputation: 6, news: 'You refused the circus. Animal lovers cheer!' } },
  { text: 'Activists demand bigger habitats. Comply?', a: { label: 'Invest $8k', money: -8000, reputation: 10, prestige: 5, news: 'Habitat upgrades win public praise.' }, b: { label: 'Ignore', reputation: -6, news: 'Protesters gathered at your gate.' } },
  { text: 'A TV crew wants to film your zoo for $5,000.', a: { label: 'Allow ($5k)', money: 5000, prestige: 8, news: 'Your zoo was on TV!' }, b: { label: 'Decline', reputation: 2, news: 'You kept the zoo calm and quiet.' } },
  { text: 'A rich donor offers $20k — if you name the gift shop after him.', a: { label: 'Take it', money: 20000, prestige: -4, news: "Welcome to the 'Baron von Moneybags' gift shop..." }, b: { label: 'Decline', prestige: 3, news: 'Your integrity is admired.' } },
  { text: 'Cut animal food quality to save money?', a: { label: 'Cut costs', money: 6000, reputation: -10, news: 'Rumors about cheap feed spread fast.' }, b: { label: 'Never!', reputation: 4, news: 'Your animals eat like kings.' } },
];

const MAYOR_GOALS = [
  { id: 'crowd', text: 'Get 30 visitors in the zoo at once', check: (s) => s.visitorCount >= 30, reward: 8000 },
  { id: 'stars', text: 'Reach the next star level', init: (s) => (s.stars || 1), check: (s, base) => (s.stars || 1) > base, reward: 12000 },
  { id: 'animals', text: 'Add 3 new animals to the zoo', init: (s, w) => w.animals.animals.length, check: (s, base, w) => w.animals.animals.length >= base + 3, reward: 9000 },
];

export const PERKS = [
  { id: 'marketing', icon: '📣', label: 'City Ads', desc: '+15% visitors, forever' },
  { id: 'food', icon: '🥕', label: 'Bulk Feed', desc: '-25% animal food costs' },
  { id: 'retail', icon: '🛍️', label: 'Retail Pro', desc: '+20% stand income' },
];

export class GameplaySystem {
  constructor(world) {
    this.world = world;
    this.dayCount = 0;
    this.totalVisitors = 0;
    // Rival zoo: Safari World across town. Their stars creep up over time — beat them!
    this.rival = { name: 'Safari World', stars: 1, progress: 0, beaten: false };
    this._milestones = [100, 500, 1000, 2500];
    this._subs = [
      bus.on('sim:newDay', () => this.dailyTick()),
      bus.on('dilemma:choose', (opt) => this.applyDilemma(opt)),
      bus.on('perk:choose', (id) => this.applyPerk(id)),
      bus.on('visitor:entered', () => this.onVisitor()),
    ];
  }
  dispose() { this._subs.forEach((u) => u()); }

  onVisitor() {
    this.totalVisitors++;
    if (this._milestones.includes(this.totalVisitors)) {
      const bonus = this.totalVisitors * 5;
      store.set({ money: store.get().money + bonus, prestige: store.get().prestige + 5 });
      pushNews(`Your ${this.totalVisitors.toLocaleString('en-US')}th visitor walked in — bonus $${bonus.toLocaleString('en-US')}!`, 'success');
      bus.emit('ui:celebrate', { kind: 'mission' });
    }
  }

  dailyTick() {
    const s = store.get();
    this.dayCount++;

    // Weekend rush: every 7th day, announced the day before
    const wd = this.dayCount % 7;
    if (wd === 6) pushNews('Tomorrow is Saturday — expect BIG crowds. Stock up on stands!', 'info');
    if (wd === 0 && !s.weekendRush) { store.set({ weekendRush: true }); pushNews('Weekend rush! Visitors are pouring in.', 'success'); }
    else if (wd !== 0 && s.weekendRush) store.set({ weekendRush: false });

    // Mayor request: occasionally, when none active
    if (!s.mayor && this.dayCount > 15 && Math.random() < 0.06) {
      const goal = MAYOR_GOALS[Math.floor(Math.random() * MAYOR_GOALS.length)];
      this._mayorGoal = goal;
      this._mayorBase = goal.init ? goal.init(s, this.world) : null;
      store.set({ mayor: { text: goal.text, daysLeft: 20, reward: goal.reward } });
      pushNews(`The mayor asks: ${goal.text} within 20 days — reward $${goal.reward.toLocaleString('en-US')}.`, 'info');
    } else if (s.mayor && this._mayorGoal) {
      if (this._mayorGoal.check(s, this._mayorBase, this.world)) {
        store.set({ money: s.money + s.mayor.reward, prestige: s.prestige + 8, mayor: null });
        pushNews(`Mayor's request fulfilled! Reward $${s.mayor.reward.toLocaleString('en-US')} + 8 prestige.`, 'success');
        bus.emit('ui:celebrate', { kind: 'mission' });
        this._mayorGoal = null;
      } else {
        const daysLeft = s.mayor.daysLeft - 1;
        if (daysLeft <= 0) { store.set({ mayor: null }); pushNews("The mayor's deadline passed. Maybe next time.", 'info'); this._mayorGoal = null; }
        else store.set({ mayor: { ...s.mayor, daysLeft } });
      }
    }

    // Choice dilemma: every ~20 days
    if (!s.dilemma && this.dayCount > 10 && Math.random() < 0.05) {
      const d = DILEMMAS[Math.floor(Math.random() * DILEMMAS.length)];
      store.set({ dilemma: d, speed: 0 }); // pause for the decision
    }

    // Rival zoo: grows slowly; monthly comparison news
    {
      this.rival.progress += 0.8 + Math.random() * 0.8;
      if (this.rival.progress >= 100 && this.rival.stars < 5) {
        this.rival.progress = 0;
        this.rival.stars++;
        pushNews(`${this.rival.name} now has ${this.rival.stars} stars! Don't let them win!`, 'error');
      }
      store.set({ rivalStars: this.rival.stars });
      const myStars = s.stars || 1;
      if (this.dayCount % 30 === 0) {
        if (myStars > this.rival.stars) pushNews(`Monthly review: you're BEATING ${this.rival.name} (${myStars}⭐ vs ${this.rival.stars}⭐)!`, 'success');
        else if (myStars < this.rival.stars) pushNews(`Monthly review: ${this.rival.name} is ahead (${this.rival.stars}⭐ vs your ${myStars}⭐). Step it up!`, 'info');
      }
      if (!this.rival.beaten && myStars >= 5 && this.rival.stars < 5) {
        this.rival.beaten = true;
        store.set({ money: s.money + 25000, prestige: s.prestige + 20 });
        pushNews(`YOU DID IT! ${this.rival.name} concedes — their star tiger joins YOUR zoo's story. +$25,000, +20 prestige!`, 'success');
        bus.emit('ui:celebrate', { kind: 'mission' });
      }
    }

    // Year report: every 360 days
    if (this.dayCount % 360 === 0) {
      const score = s.reputation * 0.5 + (s.stars || 1) * 12 + Math.min(30, this.totalVisitors / 50);
      const grade = score >= 85 ? 'A' : score >= 65 ? 'B' : score >= 45 ? 'C' : score >= 30 ? 'D' : 'F';
      store.set({ report: { year: s.year, grade, visitors: this.totalVisitors, stars: s.stars || 1 }, speed: 0 });
    }
  }

  applyDilemma(opt) {
    const s = store.get();
    store.set({
      dilemma: null, speed: 1,
      money: Math.max(0, s.money + (opt.money || 0)),
      reputation: Math.min(100, Math.max(5, s.reputation + (opt.reputation || 0))),
      prestige: Math.max(0, s.prestige + (opt.prestige || 0)),
    });
    pushNews(opt.news, (opt.reputation || 0) >= 0 ? 'success' : 'error');
  }

  applyPerk(id) {
    const s = store.get();
    const perks = [...(s.perks || []), id];
    store.set({ perks, report: null, speed: 1 });
    const p = PERKS.find((x) => x.id === id);
    pushNews(`Yearly perk chosen: ${p.icon} ${p.label} — ${p.desc}.`, 'success');
    bus.emit('ui:celebrate', { kind: 'mission' });
  }
}

export const hasPerk = (id) => (store.get().perks || []).includes(id);
